import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { UserPlus, Stethoscope, Hospital, FlaskConical, Pill, Ambulance, ShieldCheck, Building2 } from "lucide-react";

const KIND_FIELDS = {
  doctor: [
    { key: "name", label: "Doctor name", required: true },
    { key: "specialization", label: "Specialization", required: true, options: ["General Physician","Cardiologist","Pediatrician","Orthopedic Surgeon","Gynecologist","Neurologist","Dermatologist","ENT Specialist"] },
    { key: "experience", label: "Years experience", type: "number" },
    { key: "fee", label: "Consultation fee (₹)", type: "number" },
    { key: "address", label: "Clinic address" },
  ],
  hospital: [
    { key: "name", label: "Hospital name", required: true },
    { key: "address", label: "Address", required: true },
    { key: "phone", label: "Phone" },
    { key: "beds_general", label: "General beds", type: "number" },
    { key: "beds_icu", label: "ICU beds", type: "number" },
    { key: "beds_nicu", label: "NICU beds", type: "number" },
    { key: "beds_ventilator", label: "Ventilator beds", type: "number" },
    { key: "charge_general", label: "General bed charge/day (₹)", type: "number" },
    { key: "charge_icu", label: "ICU charge/day (₹)", type: "number" },
    { key: "charge_nicu", label: "NICU charge/day (₹)", type: "number" },
    { key: "charge_ventilator", label: "Ventilator charge/day (₹)", type: "number" },
    { key: "empanelments_csv", label: "Empanelments (comma separated)", placeholder: "CGHS, Ayushman Bharat, ECHS" },
    { key: "departments_csv", label: "Departments (comma separated)", placeholder: "Cardiology, Neurology, Oncology" },
    { key: "facilities_csv", label: "In-house facilities (comma separated)", placeholder: "Pharmacy, Diagnostic, Blood Bank" },
    { key: "registration_no", label: "Registration no." },
    { key: "license_no", label: "License no." },
    { key: "accreditation", label: "Accreditation", placeholder: "NABH, JCI" },
  ],
  lab: [
    { key: "name", label: "Lab name", required: true },
    { key: "address", label: "Address", required: true },
    { key: "phone", label: "Phone" },
    { key: "tests_csv", label: "Tests offered (comma separated)", placeholder: "CBC, Lipid Profile, HbA1c" },
    { key: "catalogue_csv", label: "Priced tests — name:price, comma separated", placeholder: "MRI Brain:6000, CT Chest:4500, Ultrasound:1200" },
    { key: "equipment_csv", label: "Equipment (comma separated)", placeholder: "MRI 1.5T, CT Scan, Ultrasound, X-Ray" },
    { key: "home_collection", label: "Home collection", type: "select", options: ["true","false"] },
  ],
  pharmacy: [
    { key: "name", label: "Pharmacy name", required: true },
    { key: "address", label: "Address", required: true },
    { key: "phone", label: "Phone" },
    { key: "delivery_time_min", label: "Delivery ETA (mins)", type: "number" },
    { key: "catalogue_csv", label: "Medicines — name:price, comma separated", placeholder: "Paracetamol 650:25, Azithromycin:120" },
  ],
  ambulance: [
    { key: "type", label: "Ambulance type", type: "select", options: ["basic","als","icu"], required: true },
    { key: "vehicle_number", label: "Vehicle number", required: true },
    { key: "driver_name", label: "Driver name", required: true },
    { key: "driver_phone", label: "Driver phone", required: true },
  ],
};

const ICONS = { doctor: Stethoscope, hospital: Hospital, lab: FlaskConical, pharmacy: Pill, ambulance: Ambulance };

export default function FieldExec() {
  const [kind, setKind] = useState("doctor");
  const [form, setForm] = useState({});
  const [partner, setPartner] = useState({ name: "", email: "", password: "", phone: "" });
  const [recent, setRecent] = useState({});
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/field/onboarded"); setRecent(data); }
    catch (err) { console.error("field onboarded load failed", err); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setForm({}); }, [kind]);

  const setF = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const setP = (k) => (e) => setPartner({ ...partner, [k]: e.target.value });

  const submit = async () => {
    const facility = { ...form };
    // Transform some fields
    if (kind === "hospital") {
      facility.beds = {
        general: Number(facility.beds_general || 0),
        icu: Number(facility.beds_icu || 0),
        nicu: Number(facility.beds_nicu || 0),
        ventilator: Number(facility.beds_ventilator || 0),
      };
      facility.bed_charges = {
        general: Number(facility.charge_general || 0),
        icu: Number(facility.charge_icu || 0),
        nicu: Number(facility.charge_nicu || 0),
        ventilator: Number(facility.charge_ventilator || 0),
      };
      delete facility.beds_general; delete facility.beds_icu; delete facility.beds_nicu; delete facility.beds_ventilator;
      delete facility.charge_general; delete facility.charge_icu; delete facility.charge_nicu; delete facility.charge_ventilator;
      facility.empanelments = (facility.empanelments_csv || "").split(",").map((s) => s.trim()).filter(Boolean);
      facility.departments = (facility.departments_csv || "").split(",").map((s) => s.trim()).filter(Boolean);
      facility.in_house_facilities = (facility.facilities_csv || "").split(",").map((s) => s.trim()).filter(Boolean);
      delete facility.empanelments_csv; delete facility.departments_csv; delete facility.facilities_csv;
      if (facility.registration_no || facility.license_no || facility.accreditation) {
        facility.registration = {
          registration_no: facility.registration_no || "",
          license_no: facility.license_no || "",
          accreditation: facility.accreditation || "",
        };
      }
      delete facility.registration_no; delete facility.license_no; delete facility.accreditation;
    }
    if (kind === "lab") {
      facility.tests = (facility.tests_csv || "").split(",").map((s) => s.trim()).filter(Boolean);
      facility.equipment = (facility.equipment_csv || "").split(",").map((s) => s.trim()).filter(Boolean);
      facility.test_catalogue = (facility.catalogue_csv || "").split(",").map((s) => s.trim()).filter(Boolean)
        .map((p) => { const [n, pr] = p.split(":"); return { name: (n || "").trim(), category: "Diagnostic", price: Number((pr || "0").trim()) || 0 }; });
      facility.home_collection = String(facility.home_collection) === "true";
      delete facility.tests_csv; delete facility.equipment_csv; delete facility.catalogue_csv;
    }
    if (kind === "pharmacy") {
      facility.catalogue = (facility.catalogue_csv || "").split(",").map((s) => s.trim()).filter(Boolean)
        .map((p) => { const [n, pr] = p.split(":"); return { name: (n || "").trim(), price: Number((pr || "0").trim()) || 0 }; });
      delete facility.catalogue_csv;
    }
    if (kind === "doctor") {
      facility.experience = Number(facility.experience || 0);
      facility.fee = Number(facility.fee || 500);
      facility.modes = ["in_clinic", "video"];
    }
    // required fields check
    for (const f of KIND_FIELDS[kind]) {
      if (f.required && !facility[f.key]) { toast.error(`${f.label} is required`); return; }
    }
    setLoading(true);
    try {
      const body = { kind, facility };
      if (partner.email && partner.password && partner.name) {
        body.partner_name = partner.name;
        body.partner_email = partner.email;
        body.partner_password = partner.password;
        body.partner_phone = partner.phone;
      }
      const { data } = await api.post("/field/onboard", body);
      toast.success(`Submitted for admin approval — ${data.facility?.name || data.facility?.vehicle_number} (status: ${data.approval_status})`);
      setForm({}); setPartner({ name: "", email: "", password: "", phone: "" });
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  const renderField = (f) => {
    if (f.type === "select" || f.options) {
      return (
        <Select value={form[f.key] || ""} onValueChange={setF(f.key)}>
          <SelectTrigger data-testid={`f-${f.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    return (
      <Input data-testid={`f-${f.key}`} type={f.type || "text"} value={form[f.key] || ""} onChange={setF(f.key)} placeholder={f.placeholder} />
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-24 sm:pb-8">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-600 to-teal-500 text-white flex items-center justify-center">
          <UserPlus className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-teal-700">Field Executive</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Onboard a new partner</h1>
        </div>
      </div>

      <Tabs value={kind} onValueChange={setKind}>
        <TabsList className="flex-wrap h-auto" data-testid="kind-tabs">
          {Object.keys(KIND_FIELDS).map((k) => {
            const I = ICONS[k];
            return <TabsTrigger key={k} value={k} data-testid={`tab-${k}`}><I className="w-4 h-4 mr-2" />{k.charAt(0).toUpperCase() + k.slice(1)}</TabsTrigger>;
          })}
        </TabsList>

        <TabsContent value={kind}>
          <div className="grid lg:grid-cols-2 gap-5 mt-4">
            <Card className="p-5 rounded-2xl">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">{kind} details</div>
              <div className="space-y-3">
                {KIND_FIELDS[kind].map((f) => (
                  <div key={f.key}>
                    <Label>{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>
                    {renderField(f)}
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-5 rounded-2xl">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">
                {kind === "ambulance" ? "Driver login (optional)" : "Partner login (optional)"}
              </div>
              <p className="text-xs text-slate-500 mb-3">Create credentials so this partner can log in and manage their own bookings.</p>
              <div className="space-y-3">
                <div><Label>Full name</Label><Input data-testid="p-name" value={partner.name} onChange={setP("name")} /></div>
                <div><Label>Email</Label><Input data-testid="p-email" type="email" value={partner.email} onChange={setP("email")} /></div>
                <div><Label>Password</Label><Input data-testid="p-password" type="password" value={partner.password} onChange={setP("password")} minLength={6} /></div>
                <div><Label>Phone</Label><Input data-testid="p-phone" value={partner.phone} onChange={setP("phone")} /></div>
              </div>
              <Button data-testid="onboard-submit" onClick={submit} disabled={loading} className="mt-5 w-full bg-teal-600 hover:bg-teal-500 text-white">
                <ShieldCheck className="w-4 h-4 mr-2" />
                {loading ? "Onboarding…" : `Onboard ${kind}`}
              </Button>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <div className="mt-10">
        <div className="flex items-center gap-2 mb-3">
          <Building2 className="w-4 h-4 text-teal-700" />
          <div className="font-display font-bold text-lg">Your recent onboardings</div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(recent).flatMap(([k, arr]) =>
            (arr || []).slice(0, 6).map((it) => (
              <Card key={it.id} className="p-4 rounded-xl" data-testid={`recent-${it.id}`}>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] uppercase">{k}</Badge>
                  {it.approval_status && (
                    <Badge className={`text-[10px] uppercase border-0 ${
                      it.approval_status === "approved" ? "bg-emerald-100 text-emerald-800"
                      : it.approval_status === "rejected" ? "bg-red-100 text-red-800"
                      : "bg-amber-100 text-amber-900"
                    }`}>{it.approval_status}</Badge>
                  )}
                </div>
                <div className="font-semibold mt-1">{it.name || it.vehicle_number}</div>
                <div className="text-xs text-slate-500">{it.address || it.driver_name}</div>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
